import {Timezone} from 'common/constants';
import {Translatable} from 'common/i18n';
import {TimePeriodSettings} from 'common/report/stores/types';
import {convertRelativeDateToCalendarDateAsOf, getCurrentMomentAsOf, UnitAlignmentBehavior} from 'common/util/date';

import {END_OF_TODAY} from './presets';
import {
  DateRangeAttributes,
  DateRangeType,
  ExclusionOffset,
  ExclusionPreset,
  INCOMPLETE_PERIOD_OFFSET,
  RelativeDate,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from './types';
import {getNumberOfCalendarDays, getUnitStartTooltip} from './utils';

// Used for unit-less contexts where incomplete period is never compatible (ie dashboards, Events Report, etc)
export const ORDERED_EXCLUSION_PRESETS: ExclusionPreset[] = [
  ExclusionPreset.Today,
  ExclusionPreset.CurrentWeek,
  ExclusionPreset.CurrentMonth,
  ExclusionPreset.CurrentQuarter,
];

// Used for reports that can support unit controls, such as Insights, Funnels, etc.
export const ORDERED_EXCLUSION_PRESETS_WITH_INCOMPLETE_PERIOD: ExclusionPreset[] = [
  ...ORDERED_EXCLUSION_PRESETS,
  ExclusionPreset.IncompletePeriod,
];

export const EXCLUSION_PRESET_TO_OFFSET: {
  [key in ExclusionPreset]: ExclusionOffset | null;
} = {
  [ExclusionPreset.Custom]: null,
  [ExclusionPreset.NoExclusion]: null,
  [ExclusionPreset.Today]: {value: 1, unit: TimeUnit.Day},
  [ExclusionPreset.CurrentWeek]: {value: 1, unit: TimeUnit.Week},
  [ExclusionPreset.CurrentMonth]: {value: 1, unit: TimeUnit.Month},
  [ExclusionPreset.CurrentQuarter]: {value: 1, unit: TimeUnit.Quarter},
  [ExclusionPreset.IncompletePeriod]: INCOMPLETE_PERIOD_OFFSET,
};

export const SUPPORTED_EXCLUSION_OFFSET_UNITS: TimeUnit[] = [
  TimeUnit.Minute,
  TimeUnit.Hour,
  TimeUnit.Day,
  TimeUnit.Week,
  TimeUnit.Month,
  TimeUnit.Quarter,
];

export function getExclusionDisabledReason({
  exclusion,
  isOvertimeChart,
  dateRange,
  asOfDate,
  projectTz,
  timePeriodSettings,
}: {
  exclusion: ExclusionOffset | null;
  isOvertimeChart: boolean;
  dateRange: DateRangeAttributes | null;
  asOfDate: string | null;
  projectTz: Timezone;
  timePeriodSettings: TimePeriodSettings;
}): Translatable | null {
  if (!exclusion) {
    return null;
  }
  if (!dateRange) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.default-dashboard-date-range`);
  }
  // We don't allow excluding for between date ranges other than the TODAY Preset.
  if (dateRange.type === DateRangeType.Between && dateRange.to !== END_OF_TODAY) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.between-date-range`);
  }
  if (exclusion === INCOMPLETE_PERIOD_OFFSET) {
    if (!isOvertimeChart) {
      return ({t}) => t(`reports.date-range.exclusion.disabled.non-line-incomplete-period`);
    }
    return null;
  }

  const asOfMoment = getCurrentMomentAsOf(asOfDate, projectTz);
  const exclusionDateStart = convertRelativeDateToCalendarDateAsOf({
    relativeDate: exclusion as RelativeDate,
    asOfDate,
    projectTz,
    timePeriodSettings,
    unitAlignment: UnitAlignmentBehavior.Right,
  });
  const exclusionNumDays = asOfMoment.diff(exclusionDateStart, `days`);
  const dateRangeNumDays = getNumberOfCalendarDays(dateRange, asOfMoment, projectTz, timePeriodSettings);
  if (exclusionNumDays >= dateRangeNumDays) {
    return ({t}) => t(`reports.date-range.exclusion.disabled.exclusion-exceeds-date-range`);
  }

  return null;
}

/* keep in sync with EXCLUSION_PRESET_TO_OFFSET and time clause parsing in date_range.py's DateRange.from_dict*/
export function exclusionOffsetToPreset(offset: ExclusionOffset | null | undefined): ExclusionPreset {
  if (!offset) {
    return ExclusionPreset.NoExclusion;
  } else if (offset === INCOMPLETE_PERIOD_OFFSET) {
    return ExclusionPreset.IncompletePeriod;
  } else if (typeof offset === `string`) {
    throw new Error(`invalid exclusion offset ${offset}. only $incomplete_period or relative dates are accepted.`);
  }

  if (offset.value === 1) {
    if (offset.unit === TimeUnit.Day) {
      return ExclusionPreset.Today;
    } else if (offset.unit === TimeUnit.Week) {
      return ExclusionPreset.CurrentWeek;
    } else if (offset.unit === TimeUnit.Month) {
      return ExclusionPreset.CurrentMonth;
    } else if (offset.unit === TimeUnit.Quarter) {
      return ExclusionPreset.CurrentQuarter;
    }
  }

  // Custom exclusion window
  return ExclusionPreset.Custom;
}

export const EXCLUSION_MENU_ITEM_LABEL: {[key in ExclusionPreset]: Translatable} = {
  [ExclusionPreset.Custom]: () => ``, // not a valid menu item. TODO: implement UX entry point for custom exclusion
  [ExclusionPreset.NoExclusion]: () => ``, // deselection state. not a valid menu item.
  [ExclusionPreset.Today]: ({t}) => t(`reports.date-range.exclusion.today.menu-item-label`),
  [ExclusionPreset.CurrentWeek]: ({t}) => t(`reports.date-range.exclusion.current-week.menu-item-label`),
  [ExclusionPreset.CurrentMonth]: ({t}) => t(`reports.date-range.exclusion.current-month.menu-item-label`),
  [ExclusionPreset.CurrentQuarter]: ({t}) => t(`reports.date-range.exclusion.current-quarter.menu-item-label`),
  [ExclusionPreset.IncompletePeriod]: ({t}) => t(`reports.date-range.exclusion.incomplete-period.menu-item-label`),
};

export function getExcludeSelectedLabel(exclusion: ExclusionOffset | null): Translatable {
  const preset = exclusionOffsetToPreset(exclusion);
  switch (preset) {
    case ExclusionPreset.NoExclusion:
      return ({t}) => t(`reports.date-range.exclusion.title`);
    case ExclusionPreset.Custom: {
      const window = exclusion as RelativeDate;
      return ({t, print}) =>
        t(`reports.date-range.exclusion.custom.selected-label`, {
          unit: print(TimeUnitLabelTranslatable[window.unit](window.value)).toLowerCase(),
          value: window.value,
        });
    }
    default:
      return ({t}) => t(`reports.date-range.exclusion.${preset}.selected-label`);
  }
}

export function getExclusionTooltip(preset: ExclusionPreset, timePeriodSettings: TimePeriodSettings): Translatable {
  switch (preset) {
    case ExclusionPreset.CurrentWeek:
      return getUnitStartTooltip(TimeUnit.Week, timePeriodSettings);
    case ExclusionPreset.CurrentQuarter:
      return getUnitStartTooltip(TimeUnit.Quarter, timePeriodSettings);
    case ExclusionPreset.IncompletePeriod:
      return ({t}) => t(`reports.date-range.exclusion.incomplete-period.tooltip`);
  }
  return ``;
}
